import { ActionButton, ActionButtonType, Tooltip } from "@octopusdeploy/design-system-components";
import { InfoCircleIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { CompliancePolicyVersionResource, GitRef, PolicyBffResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import { useState } from "react";
import semver from "semver/preload";
import type { TemplateWizardPageProps } from "~/areas/platformHub/TemplateWizardDialogLayout";
import TemplateWizardDialogLayout from "~/areas/platformHub/TemplateWizardDialogLayout";
import type { SavePolicyParams } from "~/areas/platformHub/policies/EditPolicyPage";
import type { PolicyActivationStatus } from "~/areas/platformHub/policies/PolicyActivationStatusDialog";
import { PolicyActivationStatusDialog } from "~/areas/platformHub/policies/PolicyActivationStatusDialog";
import { PublishPolicyDialog } from "~/areas/platformHub/policies/PublishPolicyDialog";
import type { ReleaseType } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import { CommitSummaryAndDetails } from "~/areas/projects/components/VersionControl/CommitDialog";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import CustomDialog from "~/components/Dialog/CustomDialog";
export enum PolicyActionCompleted {
    Committed = "committed",
    Published = "published",
    Activated = "activated",
    Deactivated = "deactivated"
}
export enum PolicyWizardSteps {
    "Commit",
    "Publish",
    "Activate"
}
export interface CommitParams {
    commitMessage?: CommitMessageWithDetails;
}
export interface PublishParams {
    versionToPublish: string;
}
export interface ActivationStatusParams {
    version: string;
    status: PolicyActivationStatus;
}
export interface PolicyWizardSaveParams {
    type: "Wizard";
    commitParams?: CommitParams;
    publishParams?: PublishParams;
    activationStatusParams?: ActivationStatusParams;
}
interface PolicyWizardDialogProps {
    open: boolean;
    busy: boolean;
    errors: Error[];
    gitRef: GitRef;
    stepsToShow: PolicyWizardSteps[];
    getDefaultCommitMessage: () => string;
    closeDialog: () => void;
    savePolicy: (params: SavePolicyParams) => Promise<void>;
    model: PolicyBffResource;
    publishedVersions: CompliancePolicyVersionResource[];
    policyVersion?: CompliancePolicyVersionResource;
}
const defaultVersion = "1.0.0";
export function PolicyWizardDialog({ busy, errors, gitRef, open, stepsToShow, getDefaultCommitMessage, closeDialog, savePolicy, model, publishedVersions, policyVersion }: PolicyWizardDialogProps) {
    const emptyCommitMessage = { summary: "", details: "" };
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>(emptyCommitMessage);
    const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState<boolean>(false);
    const showCommit = stepsToShow.includes(PolicyWizardSteps.Commit);
    const showPublish = stepsToShow.includes(PolicyWizardSteps.Publish);
    const showActivate = stepsToShow.includes(PolicyWizardSteps.Activate);
    const latestPublishedVersion = publishedVersions[0]; // Take first one as they are ordered in a backend
    const currentVersion = publishedVersions.find((v) => v.IsActive);
    const [versionToPublish, setVersionToPublish] = useState<string>(latestPublishedVersion ? (semver.inc(latestPublishedVersion.Version, "major") ?? defaultVersion) : defaultVersion);
    const [activationStatus, setActivationStatus] = useState<PolicyActivationStatus>(policyVersion?.IsActive ? "Active" : "Inactive");
    const [releaseType, setReleaseType] = useState<ReleaseType>("Major");
    const handleSave = async (isCommitOnly: boolean) => {
        const commitParams: CommitParams | undefined = showCommit
            ? {
                commitMessage: showCommit ? commitMessage : undefined,
            }
            : undefined;
        const publishParams: PublishParams | undefined = !isCommitOnly && showPublish
            ? {
                versionToPublish,
            }
            : undefined;
        const activationStatusParams: ActivationStatusParams | undefined = !isCommitOnly && showActivate
            ? {
                version: policyVersion?.Version ?? versionToPublish,
                status: activationStatus,
            }
            : undefined;
        // State is set before the actual save as if there's an error from the save, we exit out of this function.
        setHasSaveBeenAttempted(true);
        await savePolicy({
            type: "Wizard",
            commitParams,
            publishParams,
            activationStatusParams,
        });
    };
    const pages: TemplateWizardPageProps[] = [];
    if (showCommit) {
        pages.push({
            title: "Commit",
            content: (<>
                    <CommitSummaryAndDetails onCommitMessageChanged={setCommitMessage} defaultSummary={getDefaultCommitMessage()} commitMessage={commitMessage} commitDetailsAccessibleName={`Commit details for saving the policy`} commitMessageAccessibleName={`Commit message for saving the policy`}/>

                    <p>
                        Octopus will commit the changes to the branch <code>{gitRef}</code>
                    </p>
                </>),
            actionAvailable: true,
            additionalActions: [
                <ActionButton key="Commit Only" label="Commit Only" disabled={busy} onClick={async () => {
                        await handleSave(true);
                        closeDialog();
                    }} type={ActionButtonType.Secondary}/>,
            ],
            statusBarLabel: "Commit",
            hideAdditionalActionsOnSinglePage: true,
            singlePageSaveLabel: "Commit",
            singlePageSavingLabel: "Committing...",
        });
    }
    if (showPublish) {
        pages.push({
            title: "Publish version",
            content: (<PublishPolicyDialog gitRef={gitRef} policy={model} versionToPublish={versionToPublish} setVersionToPublish={setVersionToPublish} latestPublishedVersion={latestPublishedVersion} releaseType={releaseType} setReleaseType={setReleaseType} defaultVersion={defaultVersion}/>),
            actionAvailable: !model.Warning,
            statusBarLabel: "Publish",
            singlePageSaveLabel: "Publish",
            singlePageSavingLabel: "Publishing...",
        });
    }
    if (showActivate) {
        pages.push({
            title: "Activate Version",
            additionalTitle: (<Tooltip content={"If this version is activated, the policy will be evaluated during deployments and runbook runs based on it's scope inputs. If the version is deactivated, it will not be evaluated during deployments and runbook runs"}>
                    <InfoCircleIcon size={20} color={themeTokens.color.icon.info}/>
                </Tooltip>),
            content: <PolicyActivationStatusDialog policy={model} version={policyVersion?.Version ?? versionToPublish} currentVersion={currentVersion} activationStatus={activationStatus} setActivationStatus={setActivationStatus}/>,
            actionAvailable: true,
            statusBarLabel: "Activate",
            singlePageSaveLabel: "Update",
            singlePageSavingLabel: "Updating...",
        });
    }
    const shouldShowErrors = errors && hasSaveBeenAttempted && !busy;
    return (<CustomDialog open={open} close={noOp} render={(props) => (<TemplateWizardDialogLayout onSaveClick={async () => {
                await handleSave(false);
                return true;
            }} busy={busy} errors={shouldShowErrors ? errors : undefined} savePermission={{ permission: Permission.PlatformHubEdit }} pages={pages}/>)}/>);
}
